import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import Board from 'src/app/model/product/board/board';
import Employee from 'src/app/model/product/employee';
import { SubProject } from 'src/app/model/project/sub-project';
import { RequestService } from 'src/app/services/request.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import * as moment from 'moment';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import LastSelectedPlanValues from '../../helper-interfaces/last-selected-plan-values';
import SubProjectPlannedEvent from '../../helper-interfaces/subproject-planned-event';
import { SnackService } from 'src/app/services/snack.service';

@Component({
  selector: 'admin-planning-marker-project-info',
  templateUrl: './admin-planning-marker-project-info.component.html',
  styleUrls: ['./admin-planning-marker-project-info.component.scss']
})
export class AdminPlanningMarkerProjectInfoComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnChanges {
  private static readonly SHOW_CLASS: string = '';
  private static readonly HIDE_CLASS: string = 'hidden';

  submitting: boolean = false;

  @Input()
  index: number;
  @Input()
  subProject: SubProject;
  @Input()
  placerOptions: Employee[] = [];
  @Input()
  toggleDisplay: EventEmitter<number>;
  @Input()
  updatePlanValues: EventEmitter<LastSelectedPlanValues>;
  @Input()
  alwaysDisplay: boolean = false;
  @Input()
  showSubmitPlanContent: boolean = true;

  @Output()
  backPressed = new EventEmitter<any>();
  @Output()
  subProjectPlanned = new EventEmitter<SubProjectPlannedEvent>();

  displayClass: string = AdminPlanningMarkerProjectInfoComponent.HIDE_CLASS;

  involvedCompanies: string[];
  timeBudget: string;

  selectedPlacers: Employee[] = [];
  selectedPlacementDate: moment.Moment;

  selectedPlacerNames: string;

  constructor(
    private requestService: RequestService,
    private snackService: SnackService
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.alwaysDisplay) {
      this.displayClass = AdminPlanningMarkerProjectInfoComponent.SHOW_CLASS;
    } else if (this.toggleDisplay != null) {
      this.addSubscription(this.toggleDisplay, i => {
        this.displayClass =
          i === this.index
            ? AdminPlanningMarkerProjectInfoComponent.SHOW_CLASS
            : AdminPlanningMarkerProjectInfoComponent.HIDE_CLASS;
      });
    }
    if (this.subProject.project != null) {
      this.preprocessProducts();
      this.preprocessProjectContacts();
      this.timeBudget = ProjectHelper.getTimeBudgetInHoursAndMinutes(
        this.subProject
      );
      if (this.subProject.type !== 'BaseProject') {
        this.preprocessAdjustment();
      }
      this.calculateSelectedPlacerNames();
    }
    if (this.subProject.currentConfirmedPlacementDate != null) {
      this.selectedPlacementDate = moment(
        this.subProject.currentConfirmedPlacementDate
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.alwaysDisplay) {
      this.displayClass = changes.alwaysDisplay.currentValue
        ? AdminPlanningMarkerProjectInfoComponent.SHOW_CLASS
        : AdminPlanningMarkerProjectInfoComponent.HIDE_CLASS;
    }
  }

  hide() {
    this.displayClass = AdminPlanningMarkerProjectInfoComponent.HIDE_CLASS;
    this.backPressed.emit();
  }

  private preprocessProducts() {
    let selectedPlacers: Employee[];
    if (CollectionHelper.isPopulated(this.subProject.project.products)) {
      this.subProject.project.products.forEach(p => {
        p.overviewFormat = ProductHelper.getOverviewFormat(p);
        if (p.type === 'Board') {
          ProductHelper.calculateTableBoardDisplay(<Board>p);
        }
        if (this.subProject.type === 'BaseProject') {
          if (selectedPlacers == null) {
            selectedPlacers = [];
          }
          if (
            p.productPlacement != null &&
            CollectionHelper.isPopulated(p.productPlacement.placers)
          ) {
            selectedPlacers.push(...p.productPlacement.placers);
          }
          this.selectedPlacers = selectedPlacers.filter(
            (p, i) => selectedPlacers.findIndex(p1 => p.id === p1.id) === i
          );
        }
      });
    }
  }

  private preprocessProjectContacts() {
    if (CollectionHelper.isPopulated(this.subProject.project.projectContacts)) {
      const involvedCompanies = ProjectHelper.getDtoInvolvedCompanies(
        this.subProject.project
      );
      this.involvedCompanies = !CollectionHelper.isPopulated(involvedCompanies)
        ? null
        : involvedCompanies.map(c => c.regularName);
    }
  }

  private preprocessAdjustment(): void {
    if (
      this.subProject.projectPlacement != null &&
      CollectionHelper.isPopulated(this.subProject.projectPlacement.placers)
    ) {
      this.selectedPlacers = [...this.subProject.projectPlacement.placers];
    } else {
      this.selectedPlacers = [];
    }
  }

  private calculateSelectedPlacerNames() {
    if (this.selectedPlacers != null && this.selectedPlacers.length > 1) {
      this.selectedPlacerNames = StringHelper.join(
        this.selectedPlacers.map(pl =>
          pl.user == null
            ? null
            : StringHelper.join(
                [
                  pl.user.firstName == null
                    ? null
                    : pl.user.firstName.toString(),
                  pl.user.lastName == null ? null : pl.user.lastName.toString()
                ],
                ' '
              )
        ),
        ', '
      );
    } else {
      this.selectedPlacerNames = null;
    }
  }

  placerChange(): void {
    this.calculateSelectedPlacerNames();
  }

  selectedPlacementDateChanged(event: any): void {
    this.selectedPlacementDate = event.value;
  }

  fillPlanValues(lastSelectedPlanValues: LastSelectedPlanValues) {
    this.selectedPlacers = CollectionHelper.isPopulated(this.selectedPlacers)
      ? this.selectedPlacers
      : lastSelectedPlanValues.selectedPlacers;
    this.selectedPlacementDate =
      this.selectedPlacementDate != null
        ? this.selectedPlacementDate
        : lastSelectedPlanValues.selectedPlacementDate;
  }

  planSubProject() {
    if (this.selectedPlacementDate == null) {
      this.snackService.showError('Gelieve een datum te kiezen');
      return;
    }
    if (!CollectionHelper.isPopulated(this.selectedPlacers)) {
      this.snackService.showError(
        'Gelieve minstens één installateur te kiezen'
      );
      return;
    }
    this.submitting = true;
    const request: PlanningRequest = {
      subProjectId: this.subProject.id,
      placerIds: this.selectedPlacers.map(p => p.id),
      placementDate: this.selectedPlacementDate
    };
    this.addSubscription(
      this.requestService.post(request, '/subprojects/planSubProject'),
      (subProject: SubProject) => {
        this.subProject = subProject;
        if (this.updatePlanValues != null) {
          const lastSelectedPlanValues: LastSelectedPlanValues = {
            selectedPlacers: this.selectedPlacers,
            selectedPlacementDate: this.selectedPlacementDate
          };
          this.updatePlanValues.emit(lastSelectedPlanValues);
          this.subProjectPlanned.emit({
            subProject: this.subProject,
            lastSelectedPlanValues: lastSelectedPlanValues
          });
        }
        this.snackService.showSuccess('Project is succesvol ingepland');
        this.submitting = false;
      },
      () => {
        this.submitting = false;
      }
    );
  }

  compareEntities(o1: any, o2: any): boolean {
    return GeneralHelper.compareEntities(o1, o2);
  }
}

interface PlanningRequest {
  subProjectId: number;
  placerIds: number[];
  placementDate: moment.Moment;
}
